import {
  ClickAwayListener,
  IconButton,
  Popper,
  Portal,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import DoneIcon from "@mui/icons-material/Done";
import { SimpleDateRange } from "components/Dashboard/range-selectors/SimpleDateRange";
import DateTimeRangePicker from "@wojtekmaj/react-datetimerange-picker";
import { MouseEventHandler, useEffect, useMemo, useRef, useState } from "react";
import {
  FORMAT_DATE_NUMBERS,
  ONE_DAY_OF_MS,
  ONE_HOUR_OF_MS,
} from "utils/timeUtils";
import { Cancel, ChevronRight as ForwardIcon } from "@mui/icons-material";
import { useIsDarkMode } from "store/atoms";
import ErrorBoundary from "components/SimpleComponents/Errors/ErrorBoundary";
import useWindowSize from "utils/customHooks/useWindowSize";
import {
  DateRangePickerGlobalStyles,
  DateRangePickerStyles,
} from "./DateRangePickerStyles";
import { CalendarToday } from "@mui/icons-material";
import { DateRangeContentStyles } from "./DateRangeContentStyles";
import { REACT_CALENDAR_FORMATS } from "components/Dashboard/range-selectors/RangeSelectorToggleable/DateAndShiftPickerMode/DatePickerCalendar";
import {
  PickersActionBar,
  StaticTimePicker,
  TimeView,
} from "@mui/x-date-pickers";
import { BREAKPOINT_TABLET, useMount } from "constants/constants";
import { BREAKPOINT_DATERANGEPICKER_TWO_ROWS } from "./dateRangeUtils";
import { TimeRangeFinite } from "types/types";
import { clamp } from "lodash";
import { useDateMetadataDisplay } from "./useDateMetadataDisplay";

type FlightParseableDate = string | number | Date;

const BackIcon = () => <ForwardIcon className="backIcon" />;

const BackButton = ({ onPrevious, disableStepping = false }) => (
  <Tooltip title={`Previous period`}>
    <IconButton
      className={`btn btnBack fadeOutTransition${
        disableStepping ? " fadeOut" : ""
      }`}
      onClick={onPrevious}
      size="large"
    >
      <BackIcon />
    </IconButton>
  </Tooltip>
);
const ForwardButton = ({ onNext, disableStepping = false, ...rest }) => (
  <Tooltip title={`Next period`}>
    <IconButton
      className={`btn btnForward fadeOutTransition${
        disableStepping ? " fadeOut" : ""
      }`}
      onClick={onNext}
      {...rest}
      size="large"
    >
      <ForwardIcon className="forwardIcon" />
    </IconButton>
  </Tooltip>
);

type DateRangeContentProps = {
  startDate: FlightParseableDate;
  endDate: FlightParseableDate;
  cantStepBackward: boolean;
  minDate: FlightParseableDate;
  maxDate: FlightParseableDate;
  cantStepForward: boolean;
  handleClickBtnNext: any;
  helperText?: string | null;
  submitEnabled: boolean;
  handleSubmit: (newFocusRange?: TimeRangeFinite | null | undefined) => void;
  handleReset: () => void;
  handleKeyDown?: any;
  handleClickBtnPrevious: any;
  handleChangeStartDate: (newDate: Date) => void;
  handleChangeEndDate: (newDate: Date) => void;
  autoSubmit?: boolean;
};

function toNumber(date: FlightParseableDate): number {
  return typeof date === "string"
    ? new Date(date).getTime()
    : typeof date === "number"
      ? date
      : date.getTime();
}

export const DateRangeContent = ({
  cantStepBackward,
  handleClickBtnPrevious,
  startDate,
  handleChangeStartDate,
  handleChangeEndDate,
  endDate,
  minDate,
  maxDate,
  cantStepForward,
  handleClickBtnNext,
  helperText = null as string | null,
  submitEnabled,
  handleSubmit,
  handleReset,
  handleKeyDown,
  autoSubmit,
}: DateRangeContentProps) => {
  const isPrint = useMediaQuery("print");
  const [open, setOpen] = useState(false);
  const [isDarkMode] = useIsDarkMode();
  const [step, setStep] = useState<null | "1" | "2">(null);
  const nextStep = () => {
    setStep(prev => {
      switch (prev) {
        case null:
          if (!isClockOpen) {
            setOpen(true);
          }
          return "1";
        case "1":
          return "2";
        case "2":
          setOpen(false);
          return null;
        default:
          return null;
      }
    });
  };
  const helperTextDisplay = cantStepBackward
    ? `Minimum date exceeded - ${minDate} or later only`
    : helperText;

  const { innerWidth } = useWindowSize();
  const [isClockOpen, setIsClockOpen] = useState(false);
  const [openTo, setOpenTo] = useState<[TimeView, TimeView]>([
    "hours",
    "hours",
  ]);
  const isVerticalLayout = innerWidth <= BREAKPOINT_TABLET;

  // re-render once after mount to show stuff inside <Portal>s
  const [rerenderKey, setRerenderKey] = useState(0);
  // bugfix for multiple instances of Portal content appearing
  const [shouldRenderPortals, setShouldRenderPortals] = useState(false);
  useMount(() => {
    setTimeout(() => {
      setRerenderKey(prev => prev + 1);
      setShouldRenderPortals(true);
    }, 1 * 1000);
  });

  const isTwoRowLayout = innerWidth <= BREAKPOINT_DATERANGEPICKER_TWO_ROWS;

  // to avoid re-renders from the outer component while typing, we use internal state to store the modified range
  const [modifiedRange, setModifiedRange] = useState<[number, number]>([
    toNumber(startDate),
    toNumber(endDate),
  ]);
  useEffect(() => {
    setModifiedRange([toNumber(startDate), toNumber(endDate)]);
  }, [startDate, endDate]);

  const min = useMemo(
    () => new Date(typeof minDate === "string" ? minDate : Number(minDate)),
    [minDate]
  );

  const clearEnabled =
    submitEnabled || Boolean(startDate && endDate && startDate > endDate);

  const anchorRef = useRef<any>(null);

  // to ensure calendar always appears on top of timeline, track open state in document body for CSS targeting
  useEffect(() => {
    if (open) {
      document.body.classList.add("date-picker-open");
    } else {
      document.body.classList.remove("date-picker-open");
    }
  }, [open]);

  return isPrint ? (
    <SimpleDateRange />
  ) : (
    <DateRangeContentStyles
      className="dateRangeContent"
      $isDarkMode={isDarkMode}
      onKeyDown={handleKeyDown}
      onBlur={() => {
        if (!autoSubmit) {
          handleChangeStartDate(new Date(modifiedRange[0]));
          handleChangeEndDate(new Date(modifiedRange[1]));
        }
      }}
      ref={anchorRef}
    >
      <DateRangePickerGlobalStyles
        $step={step}
        $isDarkMode={isDarkMode}
        $isClockOpen={isClockOpen}
        $isVerticalLayout={isVerticalLayout}
      />
      <ClickAwayListener
        onClickAway={() => {
          setOpen(false);
          setStep(null);
        }}
      >
        <DateRangePickerStyles
          $step={step}
          $isDarkMode={isDarkMode}
          $isClockOpen={isClockOpen}
          $isVerticalLayout={isVerticalLayout}
        >
          <ErrorBoundary id="DateRangeContent">
            <BackButton
              disableStepping={cantStepBackward}
              onPrevious={handleClickBtnPrevious}
            />
            <ForwardButton
              disableStepping={cantStepForward}
              onNext={handleClickBtnNext}
            />
            <div className="picker-and-controls">
              {startDate &&
                endDate &&
                new Date(startDate) &&
                new Date(endDate) && (
                  <DateTimeRangePicker
                    onClockOpen={() => {
                      setOpen(false);
                      setIsClockOpen(true);
                      setOpenTo(["hours", "hours"]);
                    }}
                    calendarClassName={`date-range-picker-calendar${open ? " open" : ""}`}
                    isClockOpen={false}
                    calendarType={"gregory"}
                    minDetail={"decade"}
                    showDoubleView={innerWidth > 1000}
                    minDate={min}
                    // maxDate={max} // disabled -> prevents typing some dates into start input
                    format={`${FORMAT_DATE_NUMBERS} HH:mm`}
                    showLeadingZeros={true}
                    rangeDivider={innerWidth < 420 ? "–" : "—"}
                    isCalendarOpen={open}
                    onClick={(e: any) => {
                      const clickedStartInput = (
                        e.target as HTMLElement
                      )?.parentElement?.innerHTML.includes(
                        `datetimerange_from`
                      );
                      const clickedEndInput = (
                        e.target as HTMLElement
                      )?.parentElement?.innerHTML.includes(`datetimerange_to`);

                      const nextClickedStep = () => {
                        if (clickedStartInput) {
                          setStep("1");
                        } else if (clickedEndInput) {
                          setStep("2");
                        } else {
                          nextStep();
                        }
                      };

                      if (step === null) {
                        nextClickedStep();
                      } else {
                        const classList = Array.from(
                          (e.target as HTMLElement).classList
                        );

                        const clickedHoursInput = classList.includes(
                          "react-datetimerange-picker__inputGroup__hour"
                        );
                        const clickedMinutesInput = classList.includes(
                          "react-datetimerange-picker__inputGroup__minute"
                        );
                        const clickedTimeInput =
                          clickedHoursInput || clickedMinutesInput;

                        const clickedDateInput =
                          classList.includes(
                            "react-datetimerange-picker__inputGroup__year"
                          ) ||
                          classList.includes(
                            "react-datetimerange-picker__inputGroup__month"
                          ) ||
                          classList.includes(
                            "react-datetimerange-picker__inputGroup__day"
                          );

                        if (clickedTimeInput) {
                          setOpen(false);

                          setOpenTo(
                            clickedHoursInput
                              ? ["hours", "hours"]
                              : ["minutes", "minutes"]
                          );

                          // in "vertical layout" we only show one step at a time
                          if (isVerticalLayout) {
                            nextClickedStep();
                          }
                          setIsClockOpen(true);
                        } else if (clickedDateInput) {
                          setIsClockOpen(false);
                          setOpen(true);
                        }
                      }
                    }}
                    onClickDay={value => {
                      if (step === "1") {
                        setModifiedRange(prev =>
                          clampRange([value.getTime(), prev[1]], maxDate)
                        );
                        handleChangeStartDate(value);
                        setStep("2");
                      } else if (step === "2") {
                        setModifiedRange(prev => [prev[0], toNumber(value)]);
                        handleChangeEndDate(value);
                      }
                    }}
                    className={"date-range-picker"}
                    tileClassName={({ date, view }) => {
                      return date.getTime() > Date.now()
                        ? "future-calendar-tile"
                        : "";
                    }}
                    value={
                      modifiedRange[0] && modifiedRange[1]
                        ? [
                            new Date(modifiedRange[0]),
                            new Date(modifiedRange[1]),
                          ]
                        : [new Date(), new Date()]
                    }
                    onChange={newRange => {
                      if (!newRange) {
                        return;
                      }
                      setModifiedRange(
                        clampRange([newRange[0], newRange[1]], maxDate)
                      );
                    }}
                    onCalendarOpen={() => {
                      if (isClockOpen) {
                        setIsClockOpen(false);
                      }
                    }}
                    onCalendarClose={() => {
                      if (!isClockOpen) {
                        setStep(null);
                        setIsClockOpen(false);
                      }
                    }}
                    clearIcon={
                      isTwoRowLayout || autoSubmit ? null : (
                        <ClearButton
                          {...{
                            clearEnabled,
                            handleReset: e => {
                              e.stopPropagation(); // prevent onBlur from negating the reset & calendar/clock opening
                              handleReset();
                            },
                          }}
                        />
                      )
                    }
                    calendarIcon={
                      isTwoRowLayout ? null : (
                        <Tooltip
                          title="Select date range"
                          disableInteractive={true}
                        >
                          <CalendarToday />
                        </Tooltip>
                      )
                    }
                    {...REACT_CALENDAR_FORMATS}
                  />
                )}
              {helperTextDisplay && (
                <div className="helper-text">
                  {"*"}
                  {helperTextDisplay}
                </div>
              )}
              {isTwoRowLayout && !autoSubmit && (
                <div className="daterange-picker-controls">
                  <ClearButton
                    {...{
                      clearEnabled,
                      handleReset: e => {
                        e.stopPropagation(); // prevent onBlur from negating the reset & calendar/clock opening
                        handleReset();
                      },
                    }}
                  />
                  <Tooltip title="Select date range" disableInteractive={true}>
                    <IconButton
                      onClick={() => {
                        setOpen(prev => !prev);
                      }}
                    >
                      <CalendarToday />
                    </IconButton>
                  </Tooltip>
                </div>
              )}
            </div>

            {startDate && endDate && (
              <Popper
                open={isClockOpen && !open}
                anchorEl={anchorRef.current}
                className="time-pickers-popper"
              >
                <div
                  className={`time-pickers-wrapper ${
                    isClockOpen && !open ? "" : "hidden"
                  } step-${step}`}
                >
                  {isClockOpen && !open && (
                    <>
                      {(step === "1" || !isVerticalLayout) && (
                        <StaticTimePicker
                          className="start-time-picker"
                          ampm={false}
                          ampmInClock={false}
                          value={new Date(startDate).getTime()}
                          onChange={newValue => {
                            let newStartDate = startDate;
                            if (openTo[0] === "hours") {
                              // if we're clicking hours, only change hours
                              const hours = new Date(
                                Number(newValue)
                              ).getHours();
                              newStartDate = new Date(startDate).setHours(
                                hours
                              );
                              setOpenTo(prev => ["minutes", prev[1]]);
                            } else if (openTo[0] === "minutes") {
                              // if we're clicking minutes, only change minutes
                              const minutes = new Date(
                                Number(newValue)
                              ).getMinutes();
                              newStartDate = new Date(startDate).setMinutes(
                                minutes
                              );
                              setOpenTo(prev => ["hours", prev[1]]);
                              setIsClockOpen(false);
                            }

                            newStartDate = new Date(Number(newStartDate));
                            handleChangeStartDate(newStartDate);
                            setModifiedRange(prev => [
                              toNumber(newStartDate),
                              prev[1],
                            ]);
                          }}
                          openTo={openTo[0]}
                          onAccept={
                            isVerticalLayout
                              ? () => {
                                  setStep(null);
                                  setIsClockOpen(false);
                                }
                              : undefined
                          }
                          onClose={
                            isVerticalLayout
                              ? () => {
                                  setStep(null);
                                  setIsClockOpen(false);
                                }
                              : undefined
                          }
                          slots={{
                            actionBar: () =>
                              isVerticalLayout ? (
                                <PickersActionBar
                                  onCancel={() => {
                                    setStep(null);
                                    setIsClockOpen(false);
                                  }}
                                  onAccept={() => {
                                    return;
                                  }}
                                  onClear={() => {
                                    return;
                                  }}
                                  onSetToday={() => {
                                    return;
                                  }}
                                />
                              ) : null,
                          }}
                        />
                      )}
                      {(step === "2" || !isVerticalLayout) && (
                        <StaticTimePicker
                          className="end-time-picker"
                          ampm={false}
                          ampmInClock={false}
                          value={new Date(endDate).getTime()}
                          onChange={newValue => {
                            let newEndDate = endDate;
                            if (openTo[1] === "hours") {
                              // if we're clicking hours, only change hours
                              const hours = new Date(
                                Number(newValue)
                              ).getHours();
                              newEndDate = new Date(endDate).setHours(hours);
                              setOpenTo(prev => [prev[0], "minutes"]);
                            } else if (openTo[1] === "minutes") {
                              // if we're clicking minutes, only change minutes
                              const minutes = new Date(
                                Number(newValue)
                              ).getMinutes();
                              newEndDate = new Date(endDate).setMinutes(
                                minutes
                              );
                              setOpenTo(prev => [prev[0], "hours"]);
                              setIsClockOpen(false);
                            }

                            newEndDate = new Date(Number(newEndDate));
                            handleChangeEndDate(newEndDate);
                            setModifiedRange(prev => [
                              prev[0],
                              toNumber(newEndDate),
                            ]);
                          }}
                          onAccept={() => {
                            setStep(null);
                            setIsClockOpen(false);
                          }}
                          onClose={() => {
                            setStep(null);
                            setIsClockOpen(false);
                          }}
                          openTo={openTo[1]}
                        />
                      )}
                    </>
                  )}
                </div>
              </Popper>
            )}
          </ErrorBoundary>

          {shouldRenderPortals ? (
            <>
              {autoSubmit ? null : (
                <Portal
                  key={`portal-1-${rerenderKey}`}
                  container={document.querySelector(
                    isTwoRowLayout
                      ? ".daterange-picker-controls"
                      : ".react-datetimerange-picker__wrapper"
                  )}
                >
                  <Tooltip title="Submit">
                    <IconButton
                      color="inherit"
                      className={`date-range-btn btnSubmit ${
                        submitEnabled ? "" : "disabled"
                      }`}
                      onClick={() => {
                        if (!submitEnabled) {
                          return;
                        }
                        setStep(null);
                        setIsClockOpen(false);
                        if (modifiedRange[0] && modifiedRange[1]) {
                          // we typed into the date picker inputs - use modifiedRange in this component (which tracks input state without interrupting typing)
                          const nextFocusRange = {
                            ge: new Date(modifiedRange[0]).getTime(),
                            lt: new Date(modifiedRange[1]).getTime(),
                          };
                          handleSubmit(nextFocusRange);
                        } else {
                          // we clicked through the date/time picker popups - use focusRangeDisplay in parent component
                          handleSubmit();
                        }
                        setOpen(false);
                      }}
                      size="large"
                      style={
                        submitEnabled
                          ? {}
                          : {
                              opacity: 0.25,
                              cursor: "default",
                              pointerEvents: "none",
                            }
                      }
                    >
                      <DoneIcon />
                    </IconButton>
                  </Tooltip>
                </Portal>
              )}
              <Portal
                key={`portal-2-${rerenderKey}`}
                container={document.querySelector(
                  ".react-datetimerange-picker__inputGroup:not(:first-child)"
                )}
              >
                <div
                  className={`duration-indicator ${
                    open || isClockOpen ? "hidden" : ""
                  }`}
                >
                  {"("}
                  {startDate && endDate
                    ? formatDuration(
                        new Date(endDate).getTime() -
                          new Date(startDate).getTime()
                      )
                    : "--"}
                  {")"}
                </div>
              </Portal>
              <Portal
                key={`${rerenderKey} ${startDate}${endDate}`}
                container={document.querySelector(
                  ".react-datetimerange-picker__inputGroup:first-child"
                )}
              >
                {" "}
                <TimeZoneSettingOffsetText />
              </Portal>
            </>
          ) : null}
        </DateRangePickerStyles>
      </ClickAwayListener>
    </DateRangeContentStyles>
  );
};

export function TimeZoneSettingOffsetText({
  style,
}: {
  style?: React.CSSProperties;
}) {
  const { dayStartMsOffsetDisplay, timezoneDisplay } = useDateMetadataDisplay();

  return (
    <div style={style} className="offsetAndTimezoneIndicator">
      {dayStartMsOffsetDisplay}
      {timezoneDisplay}
    </div>
  );
}
const formatDuration = (timeMs: number) => {
  const days = Math.floor(timeMs / ONE_DAY_OF_MS);
  const hours = Math.floor((timeMs % ONE_DAY_OF_MS) / ONE_HOUR_OF_MS);
  const hoursS = hours === 1 ? "" : "s";
  const daysS = days === 1 ? "" : "s";
  return days < 1
    ? `${hours} hour${hoursS}`
    : `${days} day${daysS}, ${hours} hour${hoursS}`;
};

function ClearButton({
  clearEnabled,
  handleReset,
}: {
  clearEnabled: boolean;
  handleReset: MouseEventHandler<HTMLDivElement>;
}) {
  return (
    <Tooltip title="Clear">
      <div
        className={`btn-clear-wrapper${clearEnabled ? "" : " disabled"}`}
        style={
          clearEnabled
            ? {}
            : {
                opacity: 0.15,
                cursor: "default",
              }
        }
        onClick={handleReset}
      >
        <Cancel />
      </div>
    </Tooltip>
  );
}

function clampRange([ge, lt]: [number, number], maxDate): [number, number] {
  const lower = 0; // allow lt < ge temporarily e.g. while typing
  const upper = new Date(`${maxDate}`).getTime() + ONE_HOUR_OF_MS; // add an hour to max date to account for potential DST offset issues & not harmful to allow selecting 1h into the future;
  return [clamp(ge, lower, upper), clamp(lt, lower, upper)];
}
